import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Play, RotateCcw, Square, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { ConclaveEventShell } from '@/components/conclave/ConclaveEventShell';
import { ConclaveSeatingBoard } from '@/components/conclave/ConclaveSeatingBoard';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { conclaveApi, type ConclaveConfig, type ConclaveSpeakingState } from '@/lib/conclave-api';
import {
  formatSpeakingClock,
  sessionRemainingSeconds,
  speakingClockTone,
} from '@/components/conclave/speaking-clock';
import { conclaveEventPath } from '@/lib/conclave-paths';

type Session = {
  id: string;
  index: number;
  status: 'PLANNED' | 'ACTIVE' | 'COMPLETED';
  startedAt?: string | null;
  endedAt?: string | null;
};

type RubricBoost = { field: string; weight: number };

type StoredRubric = {
  sameCity?: number;
  giveAskOverlap?: number;
  connectCategoryOverlap?: number;
  dreamReferralOverlap?: number;
  softSeatPref?: number;
  antiRepeat?: number;
  fieldEqualityBoosts?: RubricBoost[];
};

type Analytics = {
  totals?: Record<string, number>;
  recentEvents?: Array<{ id: string; type: string; createdAt: string }>;
  leadRounds?: Array<{
    sessionId: string | null;
    sessionIndex: number | null;
    total: number;
    sameTable: number;
    byTable: Array<{ tableNumber: number; count: number }>;
  }>;
};

type ReshufflePreview = { seated: number; tables: number; repeatedPairs: number; attempts: number };

type Confirm =
  | { kind: 'complete'; session: Session }
  | { kind: 'reset' }
  | { kind: 'rebuild' }
  | { kind: 'reshuffle'; session: Session; preview: ReshufflePreview };

type LastRun = { mode: 'remaining' | 'all'; usedAi: boolean; at: Date };

const SETTINGS_KEYS = [
  'tableCount',
  'seatsPerTable',
  'seatsMin',
  'seatsMax',
  'sessionCount',
  'canSeeOtherUsers',
  'canSeeGivesWithoutContact',
  'seatPreferenceEnabled',
  'includeAllRegistrations',
  'sessionTimerMinutes',
  'userTimerMinutes',
  'dataExpiryAt',
  'matchmakingBrief',
] as const;

function settingsSnapshot(config: Partial<ConclaveConfig>): string {
  return JSON.stringify(SETTINGS_KEYS.map((key) => config[key] ?? null));
}

function minutesOrNull(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(value)) return null;
  const minutes = Math.round(value);
  if (minutes < 1) return null;
  return Math.min(240, minutes);
}

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

function statusLabel(status: Session['status']): string {
  if (status === 'ACTIVE') return 'Ongoing';
  if (status === 'COMPLETED') return 'Completed';
  return 'Yet to start';
}

function statusClass(status: Session['status']): string {
  if (status === 'ACTIVE') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (status === 'COMPLETED') return 'bg-muted text-muted-foreground border-border';
  return 'bg-amber-50 text-amber-900 border-amber-300';
}

function clockTime(iso?: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function rubricLines(rubric: StoredRubric | null | undefined): string[] {
  if (!rubric) return [];
  const lines = [
    `Same city: ${rubric.sameCity ?? 0}`,
    `Gives matching asks: ${rubric.giveAskOverlap ?? 0}`,
    `Seek and Your Connects: ${rubric.connectCategoryOverlap ?? 0}`,
    `Dream referral: ${rubric.dreamReferralOverlap ?? 0}`,
    `Seat preferences: ${rubric.softSeatPref ?? 0}`,
    `Avoid repeat pairs: ${rubric.antiRepeat ?? 0}`,
  ];
  for (const boost of rubric.fieldEqualityBoosts ?? []) {
    const name = boost.field.replace(/^onboarding:/, 'Onboarding: ');
    const verb = boost.weight < 0 ? 'Keep apart' : 'Bring together';
    lines.push(`${verb} (${name}): ${Math.abs(boost.weight)}`);
  }
  return lines;
}

export default function ConclaveOrganisePage() {
  const { eventId = '', communityId } = useParams();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [config, setConfig] = useState<Partial<ConclaveConfig>>({});
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [speakingState, setSpeakingState] = useState<ConclaveSpeakingState | null>(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const clockOffsetRef = useRef(0);
  const activeSessionId = sessions.find((session) => session.status === 'ACTIVE')?.id ?? null;
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [boardKey, setBoardKey] = useState(0);
  const [pending, setPending] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [lastRun, setLastRun] = useState<LastRun | null>(null);

  async function load() {
    try {
      const [configRes, sessionsRes, analyticsRes] = await Promise.all([
        conclaveApi.getConfig(eventId),
        conclaveApi.listSessions(eventId),
        conclaveApi.analytics(eventId),
      ]);
      setConfig(configRes.config);
      setSavedSnapshot(settingsSnapshot(configRes.config));
      setSessions((sessionsRes.sessions as Session[]) || []);
      setAnalytics(analyticsRes);
      setForbidden(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) setForbidden(true);
      else throw err;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch {
        if (!cancelled) toast.error('Could not load the organiser page');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const ongoing = sessions.find((s) => s.status === 'ACTIVE') ?? null;
  const completedCount = sessions.filter((s) => s.status === 'COMPLETED').length;
  const hasStartedSessions = sessions.some((s) => s.status !== 'PLANNED');
  const seatsAssigned = analytics?.totals?.assignments ?? 0;
  const settingsDirty = settingsSnapshot(config) !== savedSnapshot;
  const rubric = config.matchmakingRubric as StoredRubric | null | undefined;
  const rubricDescription = useMemo(() => rubricLines(rubric), [rubric]);

  function refreshBoard() {
    setBoardKey((k) => k + 1);
  }

  async function saveSettings(e?: FormEvent) {
    e?.preventDefault();
    setPending('save');
    try {
      const res = await conclaveApi.patchConfig(eventId, {
        tableCount: config.tableCount ?? null,
        seatsPerTable: config.seatsPerTable ?? null,
        seatsMin: config.seatsMin ?? null,
        seatsMax: config.seatsMax ?? null,
        sessionCount: config.sessionCount ?? 1,
        canSeeOtherUsers: config.canSeeOtherUsers ?? true,
        canSeeGivesWithoutContact: config.canSeeGivesWithoutContact ?? true,
        seatPreferenceEnabled: config.seatPreferenceEnabled ?? false,
        includeAllRegistrations: config.includeAllRegistrations ?? false,
        sessionTimerMinutes: minutesOrNull(config.sessionTimerMinutes),
        userTimerMinutes: minutesOrNull(config.userTimerMinutes),
        dataExpiryAt: config.dataExpiryAt ?? null,
        matchmakingBrief: config.matchmakingBrief ?? null,
      } as Partial<ConclaveConfig>);
      setConfig(res.config);
      setSavedSnapshot(settingsSnapshot(res.config));
      toast.success('Settings saved. Seating runs use these values from now on.');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the settings'));
    } finally {
      setPending(null);
    }
  }

  async function startSession(session: Session) {
    setPending(`start:${session.id}`);
    try {
      await conclaveApi.startSession(eventId, session.id);
      toast.success(`Session ${session.index + 1} started. Attendees see it in Live.`);
      await load();
      refreshBoard();
    } catch (err) {
      toast.error(errorMessage(err, `Could not start session ${session.index + 1}`));
    } finally {
      setPending(null);
    }
  }

  async function completeSession(session: Session) {
    setPending(`complete:${session.id}`);
    try {
      await conclaveApi.completeSession(eventId, session.id);
      toast.success(
        `Session ${session.index + 1} completed. Attendees have left the live session.`
      );
      await load();
      refreshBoard();
    } catch (err) {
      toast.error(errorMessage(err, `Could not complete session ${session.index + 1}`));
    } finally {
      setPending(null);
    }
  }

  async function resetSessions() {
    setPending('reset');
    try {
      await conclaveApi.resetSessions(eventId);
      toast.success('All sessions are back to Yet to start.');
      await load();
      refreshBoard();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not reset the sessions'));
    } finally {
      setPending(null);
    }
  }

  async function generateSeating(mode: 'remaining' | 'all') {
    setPending(`generate:${mode}`);
    try {
      const res = await conclaveApi.generateSeating(eventId, mode);
      setSessions((res.sessions as Session[]) || []);
      setLastRun({ mode, usedAi: Boolean(res.usedAiRubric), at: new Date() });
      toast.success(
        `${mode === 'all' ? 'Seating rebuilt' : 'Seating updated'} ${
          res.usedAiRubric ? 'with the AI rubric' : 'with the baseline rules'
        }.`
      );
      await load();
      refreshBoard();
    } catch (err) {
      toast.error(errorMessage(err, 'Seating could not be generated'));
    } finally {
      setPending(null);
    }
  }

  useEffect(() => {
    if (!activeSessionId) {
      setSpeakingState(null);
      return;
    }
    let cancelled = false;
    const fetchState = async () => {
      try {
        const res = await conclaveApi.speaking(eventId, activeSessionId);
        if (cancelled) return;
        clockOffsetRef.current = new Date(res.state.serverNow).getTime() - Date.now();
        setSpeakingState(res.state);
      } catch {
        /* keep the last known state */
      }
    };
    void fetchState();
    const poll = window.setInterval(() => void fetchState(), 10_000);
    const tick = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [eventId, activeSessionId]);

  async function previewReshuffle(session: Session) {
    setPending(`reshuffle:${session.id}`);
    try {
      const res = await conclaveApi.reshuffleSession(eventId, session.id, true);
      setConfirm({
        kind: 'reshuffle',
        session,
        preview: {
          seated: res.seated,
          tables: res.tables,
          repeatedPairs: res.repeatedPairs,
          attempts: res.attempts,
        },
      });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not preview the reshuffle'));
    } finally {
      setPending(null);
    }
  }

  async function applyReshuffle(session: Session) {
    setPending(`reshuffle:${session.id}`);
    try {
      const res = await conclaveApi.reshuffleSession(eventId, session.id, false);
      toast.success(
        `Session ${session.index + 1} reshuffled: ${res.seated} people at ${res.tables} tables, ${res.repeatedPairs} repeated pairs.`
      );
      await load();
      refreshBoard();
    } catch (err) {
      toast.error(errorMessage(err, 'Reshuffle failed'));
    } finally {
      setPending(null);
    }
  }

  function runConfirm() {
    if (!confirm) return;
    const action = confirm;
    setConfirm(null);
    if (action.kind === 'complete') void completeSession(action.session);
    if (action.kind === 'reset') void resetSessions();
    if (action.kind === 'rebuild') void generateSeating('all');
    if (action.kind === 'reshuffle') void applyReshuffle(action.session);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <ConclaveEventShell>
        <p className="mt-6 text-muted-foreground">
          You need to be an event organiser to open this page.
        </p>
        <Button variant="link" className="mt-4 px-0" asChild>
          <Link to={conclaveEventPath(eventId, communityId)}>Back to event</Link>
        </Button>
      </ConclaveEventShell>
    );
  }

  const busy = pending !== null;

  return (
    <ConclaveEventShell title="Organiser" maxWidthClassName="max-w-6xl">
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 md:mt-6">
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs uppercase text-muted-foreground">Sessions</p>
          <p className="text-lg font-semibold">
            {completedCount} of {sessions.length} completed
          </p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs uppercase text-muted-foreground">Now</p>
          <p className="text-lg font-semibold">
            {ongoing ? `Session ${ongoing.index + 1} is live` : 'No session running'}
          </p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs uppercase text-muted-foreground">Seating</p>
          <p className="text-lg font-semibold">
            {seatsAssigned > 0 ? `${seatsAssigned} seats assigned` : 'Not generated yet'}
          </p>
        </div>
      </div>

      <Card className="glass-card mt-6 border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="space-y-4 px-3 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="font-medium">Sessions</h2>
              <p className="text-sm text-muted-foreground">
                Run one session at a time. Starting is blocked while another session is running.
              </p>
            </div>
            {hasStartedSessions ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-lg"
                disabled={busy}
                onClick={() => setConfirm({ kind: 'reset' })}
              >
                {pending === 'reset' ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-1.5 h-4 w-4" />
                )}
                Reset sessions
              </Button>
            ) : null}
          </div>

          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No sessions yet. Set the session count in the settings below.
            </p>
          ) : (
            <ul className="divide-y">
              {sessions.map((session) => {
                const startedAt = clockTime(session.startedAt);
                const endedAt = clockTime(session.endedAt);
                const canStart = session.status === 'PLANNED';
                const canComplete = session.status === 'ACTIVE';
                const startBlocked = Boolean(ongoing && ongoing.id !== session.id);
                return (
                  <li
                    key={session.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">Session {session.index + 1}</span>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(session.status)}`}
                        >
                          {statusLabel(session.status)}
                        </span>
                      </div>
                      {startedAt ? (
                        <p className="text-xs text-muted-foreground">
                          Started {startedAt}
                          {endedAt ? ` · ended ${endedAt}` : ''}
                        </p>
                      ) : null}
                      {session.status === 'ACTIVE' && speakingState?.sessionId === session.id ? (
                        <SessionClock
                          state={speakingState}
                          now={clockNow}
                          offsetMs={clockOffsetRef.current}
                        />
                      ) : null}
                      {canStart && startBlocked && ongoing ? (
                        <p className="text-xs text-amber-800">
                          Complete Session {ongoing.index + 1} before starting this one.
                        </p>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                      {canStart ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          disabled={busy}
                          onClick={() => void previewReshuffle(session)}
                        >
                          {pending === `reshuffle:${session.id}` ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : null}
                          Reshuffle
                        </Button>
                      ) : null}
                      {canStart ? (
                        <Button
                          type="button"
                          size="sm"
                          className="rounded-lg"
                          disabled={busy || startBlocked}
                          onClick={() => void startSession(session)}
                        >
                          {pending === `start:${session.id}` ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="mr-1.5 h-4 w-4" />
                          )}
                          Start
                        </Button>
                      ) : null}
                      {canComplete ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          disabled={busy}
                          onClick={() => setConfirm({ kind: 'complete', session })}
                        >
                          {pending === `complete:${session.id}` ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <Square className="mr-1.5 h-4 w-4" />
                          )}
                          Complete
                        </Button>
                      ) : null}
                      {session.status === 'COMPLETED' ? (
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4" /> Done
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="glass-card mt-6 border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="space-y-5 px-3 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
          <div>
            <h2 className="font-medium">Seating and matchmaking</h2>
            <p className="text-sm text-muted-foreground">
              The brief is turned into matching weights when seating is generated. Save the settings
              before generating, or the new values are not used.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brief">Matchmaking brief</Label>
            <Textarea
              id="brief"
              placeholder="e.g. Avoid people from the same organisation on one table; prioritise the same city."
              value={config.matchmakingBrief || ''}
              onChange={(e) => setConfig((c) => ({ ...c, matchmakingBrief: e.target.value }))}
              className="min-h-24"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Current rubric</p>
            {rubricDescription.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {rubricDescription.map((line) => (
                  <li
                    key={line}
                    className="rounded-full bg-muted px-3 py-1 text-xs text-foreground"
                  >
                    {line}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Not generated yet. The default rules are used until seating runs.
              </p>
            )}
            {lastRun ? (
              <p className="text-xs text-muted-foreground">
                Last run ({lastRun.mode === 'all' ? 'rebuild' : 'remaining'}) at{' '}
                {lastRun.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}:{' '}
                {lastRun.usedAi
                  ? 'AI rubric applied.'
                  : 'baseline rules used (no brief, or AI unavailable).'}
              </p>
            ) : null}
          </div>

          {settingsDirty ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              You have unsaved changes. Save them before generating seating.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="rounded-xl"
              disabled={busy || settingsDirty}
              onClick={() => void generateSeating('remaining')}
            >
              {pending === 'generate:remaining' ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-4 w-4" />
              )}
              Generate seating
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-amber-600 text-amber-900"
              disabled={busy || Boolean(ongoing) || settingsDirty}
              title={ongoing ? 'Not available while a session is running' : undefined}
              onClick={() => setConfirm({ kind: 'rebuild' })}
            >
              Rebuild all seating
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Generate fills sessions without seating and keeps the rest. Rebuild replaces seating for
            sessions that aren&apos;t running or finished.
            {ongoing ? ' Rebuild is unavailable while a session is running.' : ''}
          </p>
        </CardContent>
      </Card>

      <form onSubmit={saveSettings} className="mt-6">
        <Card className="glass-card border-0 shadow-none md:border md:shadow-sm">
          <CardContent className="space-y-4 px-3 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-medium">Event settings</h2>
                <p className="text-sm text-muted-foreground">
                  Tables, sessions, and what attendees can see.
                </p>
              </div>
              {settingsDirty ? (
                <span className="text-xs font-medium text-amber-800">Unsaved changes</span>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {(
                [
                  ['tableCount', 'Table count'],
                  ['seatsPerTable', 'Seats per table'],
                  ['seatsMin', 'Seats min'],
                  ['seatsMax', 'Seats max'],
                  ['sessionCount', 'Session count'],
                ] as const
              ).map(([key, labelText]) => (
                <div key={key} className="space-y-2">
                  <Label>{labelText}</Label>
                  <Input
                    type="number"
                    value={config[key] ?? ''}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        [key]: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(config.canSeeOtherUsers)}
                  onCheckedChange={(v) =>
                    setConfig((c) => ({ ...c, canSeeOtherUsers: Boolean(v) }))
                  }
                />
                Attendees can see other users
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(config.canSeeGivesWithoutContact)}
                  onCheckedChange={(v) =>
                    setConfig((c) => ({ ...c, canSeeGivesWithoutContact: Boolean(v) }))
                  }
                />
                Show gives without contact details
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(config.seatPreferenceEnabled)}
                  onCheckedChange={(v) =>
                    setConfig((c) => ({ ...c, seatPreferenceEnabled: Boolean(v) }))
                  }
                />
                Enable seat preferences
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(config.includeAllRegistrations)}
                  onCheckedChange={(v) =>
                    setConfig((c) => ({ ...c, includeAllRegistrations: Boolean(v) }))
                  }
                />
                Seat everyone registered (ignore activity check-ins)
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="session-timer">Timer per session (minutes)</Label>
                <Input
                  id="session-timer"
                  type="number"
                  min={1}
                  max={240}
                  inputMode="numeric"
                  placeholder="5"
                  value={config.sessionTimerMinutes ?? ''}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      sessionTimerMinutes: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Counts down from when the session starts, and keeps running when people start or
                  stop speaking. Leave blank to hide it.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-timer">Timer per user (minutes)</Label>
                <Input
                  id="user-timer"
                  type="number"
                  min={1}
                  max={240}
                  inputMode="numeric"
                  placeholder="5"
                  value={config.userTimerMinutes ?? ''}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      userTimerMinutes: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Counts down only while that person is speaking. Stopping pauses it, and speaking
                  again continues from the same time. Leave blank to hide it.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Data expiry</Label>
              <Input
                type="datetime-local"
                value={config.dataExpiryAt ? String(config.dataExpiryAt).slice(0, 16) : ''}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    dataExpiryAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                  }))
                }
              />
            </div>

            <Button type="submit" disabled={busy || !settingsDirty} className="rounded-xl">
              {pending === 'save' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Save settings
            </Button>
          </CardContent>
        </Card>
      </form>

      <Card className="glass-card mt-6 border-0 shadow-none md:mt-8 md:border md:shadow-sm">
        <CardContent className="space-y-3 px-3 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
          <h2 className="font-medium">Table board</h2>
          <p className="text-sm text-muted-foreground">
            Bird’s-eye view of every table. Search people, open profiles, and move seats by
            selecting a person then tapping a table.
          </p>
          <ConclaveSeatingBoard key={boardKey} eventId={eventId} sessions={sessions} />
        </CardContent>
      </Card>

      <Card className="glass-card mt-6 border-0 shadow-none md:mt-8 md:border md:shadow-sm">
        <CardContent className="px-3 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
          <h2 className="font-medium">Analytics</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            {Object.entries(analytics?.totals || {}).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-muted px-3 py-2">
                <div className="text-xs uppercase text-muted-foreground">{k}</div>
                <div className="text-xl font-semibold">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Leads passed per round
            </p>
            {(analytics?.leadRounds ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No leads passed yet.</p>
            ) : (
              (analytics?.leadRounds ?? []).map((round) => (
                <div
                  key={round.sessionId ?? 'none'}
                  className="rounded-xl bg-muted px-3 py-2 text-sm"
                >
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">
                      {round.sessionIndex !== null
                        ? `Session ${round.sessionIndex + 1}`
                        : 'Outside a session'}
                    </span>
                    <span className="text-muted-foreground">
                      {round.total} leads · {round.sameTable} at the same table
                    </span>
                  </div>
                  {round.byTable.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {round.byTable.map((table) => (
                        <span
                          key={table.tableNumber}
                          className="rounded-lg bg-background px-2 py-1 text-xs"
                        >
                          Table {table.tableNumber} · {table.count} sent
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>

          <ul className="mt-4 max-h-48 space-y-1 overflow-auto text-xs text-muted-foreground">
            {(analytics?.recentEvents || []).map((ev) => (
              <li key={ev.id}>
                {ev.type} · {String(ev.createdAt)}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm?.kind === 'complete'
            ? `Complete Session ${confirm.session.index + 1}?`
            : confirm?.kind === 'reset'
              ? 'Reset all sessions?'
              : confirm?.kind === 'reshuffle'
                ? `Reshuffle Session ${confirm.session.index + 1}?`
                : 'Rebuild all seating?'
        }
        description={
          confirm?.kind === 'complete'
            ? 'Attendees will be taken out of the live session. You can reset the sessions later.'
            : confirm?.kind === 'reset'
              ? 'Every session goes back to Yet to start, and anyone in a live session is taken out of it. Seating stays as it is.'
              : confirm?.kind === 'reshuffle'
                ? `Shuffle ${confirm.preview.seated} people into ${confirm.preview.tables} tables. ${
                    confirm.preview.repeatedPairs === 0
                      ? 'Nobody is seated with someone they shared a table with in another session.'
                      : `${confirm.preview.repeatedPairs} earlier table-mate pairs could not be avoided.`
                  } The current seating for this session is replaced.`
                : 'Seating for sessions that are not running or finished is replaced, using the saved brief and current profiles.'
        }
        confirmLabel={
          confirm?.kind === 'complete'
            ? 'Complete session'
            : confirm?.kind === 'reset'
              ? 'Reset'
              : confirm?.kind === 'reshuffle'
                ? 'Reshuffle'
                : 'Rebuild seating'
        }
        cancelLabel="Cancel"
        destructive={confirm?.kind !== 'complete'}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </ConclaveEventShell>
  );
}

function SessionClock({
  state,
  now,
  offsetMs,
}: {
  state: ConclaveSpeakingState;
  now: number;
  offsetMs: number;
}) {
  const remaining = sessionRemainingSeconds(state, now, offsetMs);
  if (remaining != null) {
    const total = (state.sessionTimerMinutes ?? 0) * 60;
    const tone = speakingClockTone(remaining, total);
    return (
      <p
        className={`text-sm font-semibold tabular-nums ${
          tone === 'overtime'
            ? 'text-red-600'
            : tone === 'warning'
              ? 'text-amber-700'
              : 'text-foreground'
        }`}
      >
        Time left {formatSpeakingClock(remaining)}
      </p>
    );
  }
  if (!state.sessionStartedAt) return null;
  const elapsed = Math.max(
    0,
    Math.floor((now + offsetMs - new Date(state.sessionStartedAt).getTime()) / 1000)
  );
  return (
    <p className="text-sm text-muted-foreground tabular-nums">
      Running {formatSpeakingClock(elapsed)} · no session timer set
    </p>
  );
}
